import { ApolloProvider } from '@apollo/client';
import { useMatchRoute, useNavigate } from '@tanstack/react-location';
import { Badge, Button, Dropdown, Menu as AntMenu } from 'antd';
import PropTypes from 'prop-types';
import React, { forwardRef, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider, useStore } from 'react-redux';
import styled from 'styled-components';

import {
  SubscribeToNotificationsDocument,
  SubscribeToNotificationsSubscription,
  SubscribeToNotificationsSubscriptionVariables,
  useGetNotificationsQuery,
  useReadNotificationMutation,
} from '@/generated/graphql';
import useAppDispatch from '@/hooks/useAppDispatch';
import useNotification from '@/hooks/useNotification';
import apolloClient from '@/main/apollo';
import { updateUnreadNotifications } from '@/state/customer/actions';
import { toNotifications } from '@/views/Notifications/routes';

//import Menu from './Menu';
import NotificationItem from './NotificationItem';

interface Props {
  children?: ReactNode;
}

const ButtonMenuItem = styled(AntMenu.Item)`
  &:hover {
    background-color: inherit;
  }
`;

const StyledMenu = styled(AntMenu)`
  .ant-dropdown-menu-item-group-list {
    margin: 0;
  }
`;
const StyledMenuItem = styled(AntMenu.Item)<{ $read: boolean }>`
  border-bottom: 1px solid #f0f0f0;
  background-color: ${props => (props.$read ? 'inherit' : '#f5f5f5')};
`;

// Wrapper component to ensure proper ref forwarding for Dropdown
const BadgeWrapper = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    count: number;
    overflowCount: number;
    offset: [number, number];
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  }
>(({ children, count, overflowCount, offset, onClick }, ref) => (
  <div ref={ref} onClick={onClick}>
    <Badge count={count} overflowCount={overflowCount} offset={offset}>
      {children}
    </Badge>
  </div>
));

BadgeWrapper.displayName = 'BadgeWrapper';

BadgeWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  count: PropTypes.number.isRequired,
  overflowCount: PropTypes.number.isRequired,
  offset: PropTypes.arrayOf(PropTypes.number).isRequired,
  onClick: PropTypes.func,
};

function Notifications({ children }: Props): React.ReactElement {
  const store = useStore();
  const notification = useNotification();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const isOnNotificationsPage = !!matchRoute({ to: toNotifications() });
  const { data, subscribeToMore, loading } = useGetNotificationsQuery({
    variables: {
      limit: 6,
      skip: 0,
    },
  });
  const [action] = useReadNotificationMutation();

  useEffect(() => {
    const unsubscribe = subscribeToMore<
      SubscribeToNotificationsSubscription,
      SubscribeToNotificationsSubscriptionVariables
    >({
      document: SubscribeToNotificationsDocument,
      updateQuery: (prev, { subscriptionData }) => {
        if (
          (!subscriptionData.data && !subscriptionData.data.newNotification) ||
          prev.notifications.notifications?.filter(
            item => item.id === subscriptionData.data.newNotification.id,
          ).length > 0
        ) {
          return prev;
        }
        notification.open({
          message: t('New Notification'),
          description: (
            <ApolloProvider client={apolloClient}>
              <Provider store={store}>
                <NotificationItem
                  item={subscriptionData.data.newNotification}
                />
              </Provider>
            </ApolloProvider>
          ),
          //onClose: close,
          placement: 'bottomRight',
        });

        const rval = {
          ...prev,
          notifications: {
            ...prev.notifications,
            notifications: [
              subscriptionData.data.newNotification,
              ...prev.notifications.notifications,
            ],
          },
        };
        return rval;
      },
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    dispatch(
      updateUnreadNotifications(
        data?.notifications.notifications.filter(note => !note.read).length ??
          0,
      ),
    );
  }, [data?.notifications.notifications.filter(note => !note.read).length]);

  if (loading) {
    return (
      <Badge count={0} overflowCount={5} offset={[-25, 12]}>
        {children}
      </Badge>
    );
  }

  if (!data || !data?.notifications) {
    return null;
  }
  const { notifications } = data.notifications;

  return (
    <div className={'tour-notifications'} style={{ width: '100%' }}>
      <Dropdown
        placement="topLeft"
        menu={{
          items: [
            ...notifications.slice(0, 5).map(item => ({
              key: item.id,
              label: <NotificationItem item={item} />,
            })),
            ...(isOnNotificationsPage
              ? []
              : [
                  {
                    key: 'button',
                    label: <Button block>{t('showAll')}</Button>,
                    onClick: () => {
                      navigate({
                        to: toNotifications(),
                      });
                    },
                  },
                ]),
          ],
        }}
        trigger={['click']}
        arrow
        align={{
          offset: [20, 10],
        }}
        onOpenChange={open => {
          if (open) {
            notifications
              .filter(note => !note.read)
              .map(item =>
                action({
                  variables: {
                    id: item.id,
                  },
                }),
              );
          }
        }}
      >
        <div
          onClick={e => {
            const target = e.target as HTMLElement;
            const linkElement = target.closest('a');
            const isBadgeClick =
              target.closest('.ant-badge') ||
              target.closest('.ant-badge-count');

            if (linkElement && !isBadgeClick) {
              e.stopPropagation();
              e.preventDefault();
              navigate({
                to: toNotifications(),
              });
            }
          }}
        >
          <BadgeWrapper
            count={notifications.filter(note => !note.read).length || 0}
            overflowCount={5}
            offset={[-25, 12]}
          >
            {children}
          </BadgeWrapper>
        </div>
      </Dropdown>
    </div>
  );
}

export default Notifications;
